import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import {
  Lock, CheckCircle2, XCircle, Loader2, Mail, Phone, MapPin, Building2, ArrowRight,
} from "lucide-react";

import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Public "Create Password" screen reached via the email invitation link
 *   /accept-invite/:token
 * Validates the token, prefills the worker's profile (read-only), and lets
 * them set a password. On success, the user account is created with status
 * `worker_pending` and the admin sees it in their Manage Employees list.
 */
export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, invitation: null, error: null });
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND}/api/auth/invitation/${token}`);
        if (!cancelled) setState({ loading: false, invitation: data, error: null });
      } catch (e) {
        if (cancelled) return;
        const status = e.response?.status;
        const detail = e.response?.data?.detail;
        let msg = detail || "This invitation link is not valid.";
        if (status === 410) msg = "This invitation has expired. Please ask your admin to send a new one.";
        else if (status === 409) msg = "This invitation has already been used.";
        else if (status === 404) msg = "This invitation was not found or has been revoked.";
        setState({ loading: false, invitation: null, error: msg });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (pw !== pw2) { toast.error("Passwords do not match"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND}/api/auth/invitation/${token}/accept`, {
        password: pw, password_confirm: pw2,
      });
      setSuccess(true);
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Could not save your password");
    } finally { setSubmitting(false); }
  };

  if (state.loading) {
    return (
      <Screen>
        <div className="flex items-center gap-3 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" /> Verifying your invitation…
        </div>
      </Screen>
    );
  }

  if (state.error) {
    return (
      <Screen>
        <div className="flex items-center gap-3 mb-4 text-destructive">
          <XCircle className="h-8 w-8" strokeWidth={1.6} />
          <div>
            <div className="text-2xl font-semibold tracking-tight" data-testid="invite-error-heading">Invitation unavailable</div>
            <div className="text-xs text-muted-foreground font-mono uppercase tracking-widest mt-0.5">accept-invite</div>
          </div>
        </div>
        <p className="text-base text-foreground mb-8" data-testid="invite-error-message">{state.error}</p>
        <Link to="/login">
          <Button variant="outline" className="rounded-sm border-border">Back to login</Button>
        </Link>
      </Screen>
    );
  }

  if (success) {
    return (
      <Screen>
        <div className="flex items-center gap-3 mb-4 text-primary">
          <CheckCircle2 className="h-8 w-8" strokeWidth={1.6} />
          <div>
            <div className="text-2xl font-semibold tracking-tight" data-testid="invite-success-heading">Password saved</div>
            <div className="text-xs text-muted-foreground font-mono uppercase tracking-widest mt-0.5">status · worker_pending</div>
          </div>
        </div>
        <p className="text-base text-foreground mb-2" data-testid="invite-success-message">
          Thanks, {state.invitation.name.split(" ")[0]}. Your admin at <strong>{state.invitation.company_name}</strong> has been notified.
        </p>
        <p className="text-sm text-muted-foreground mb-8">
          You&apos;ll be able to sign in as soon as they approve your account — usually within a few minutes.
        </p>
        <Button className="rounded-sm bg-primary hover:bg-primary/90" onClick={() => navigate("/login")} data-testid="invite-goto-login-btn">
          Go to sign in <ArrowRight className="h-4 w-4 ml-1.5" />
        </Button>
      </Screen>
    );
  }

  const inv = state.invitation;
  return (
    <Screen>
      <div className="overline mb-2">{inv.company_name} · Team invitation</div>
      <div className="flex items-center gap-3 mb-2 text-primary">
        <Lock className="h-8 w-8" strokeWidth={1.6} />
        <div className="text-2xl font-semibold tracking-tight" data-testid="invite-heading">Create your password</div>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        {inv.invited_by_name ? `${inv.invited_by_name} added you to ` : "You've been added to "}
        <strong>{inv.company_name}</strong> as a <span className="font-mono">{inv.role}</span>.
        Set a password to finish setting up your account.
      </p>

      <div className="border border-border rounded-sm p-4 bg-muted/30 mb-6 text-sm">
        <MetaLine icon={Building2} label="Company" value={inv.company_name} />
        <MetaLine icon={Mail} label="Email" value={inv.email} testid="invite-email-readonly" />
        <MetaLine icon={Phone} label="Phone" value={inv.phone || "—"} />
        <MetaLine icon={MapPin} label="Address" value={inv.address || "—"} />
      </div>

      <form onSubmit={submit} className="space-y-3" data-testid="invite-form">
        <label className="block">
          <span className="overline block mb-1">Password</span>
          <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 6 characters" required minLength={6} className="rounded-sm" data-testid="invite-password-input" />
        </label>
        <label className="block">
          <span className="overline block mb-1">Confirm password</span>
          <Input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required minLength={6} className="rounded-sm" data-testid="invite-password-confirm-input" />
        </label>
        <Button type="submit" className="rounded-sm bg-primary hover:bg-primary/90 w-full" disabled={submitting} data-testid="invite-submit-btn">
          {submitting ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Saving…</> : <>Set my password <ArrowRight className="h-4 w-4 ml-1.5" /></>}
        </Button>
      </form>
    </Screen>
  );
}

function Screen({ children }) {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6" data-testid="accept-invite-screen">
      <div className="max-w-md w-full">{children}</div>
    </div>
  );
}

function MetaLine({ icon: Icon, label, value, testid }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <span className="text-muted-foreground flex items-center gap-2 text-xs uppercase tracking-widest font-mono"><Icon className="h-3 w-3" /> {label}</span>
      <span className="font-mono text-sm truncate" data-testid={testid}>{value}</span>
    </div>
  );
}
